// Per-object live-resize override store.
//
// WHY: resizing an object used to dispatch to redux on every frame. Each dispatch
// re-renders the whole ~3900-line MainCanvas synchronously (flushSync) — that
// reconciliation is the resize lag (confirmed: type- and count-independent, no
// repaint, only the resized object commits, drag is smooth only because it
// dispatches 0 times mid-gesture).
//
// HOW: during a resize gesture ItemDragger pushes the live geometry for the ONE
// resized object here instead of dispatching. Only that object's renderer
// (subscribed via useLiveResize) re-renders — React still lays out the new
// geometry correctly (true reflow, every type), but MainCanvas never subscribes
// to this store, so it does NOT re-render. On gesture end the final value is
// committed to redux ONCE (history checkpoint) and the override is cleared.
import { useSyncExternalStore } from "react";

const overrides = new Map(); // id -> partial override ({ width, height, image, font, transform })
const listeners = new Map(); // id -> Set<() => void>

function emit(id) {
  const subs = listeners.get(id);
  // TEMP PROBE: count how many components are subscribed to this id. A runaway
  // "Maximum update depth" means some subscriber re-renders in a cycle; the
  // subscriber COUNT climbing during a gesture means components are mounting/
  // remounting per frame (each mount re-subscribes), which is the usual cause.
  if (typeof window !== "undefined" && window.__probeLive) {
    const n = subs ? subs.size : 0;
    const rec = (window.__liveStats = window.__liveStats || {});
    rec[id] = rec[id] || { emits: 0, maxSubs: 0, subsSeries: [] };
    rec[id].emits += 1;
    rec[id].maxSubs = Math.max(rec[id].maxSubs, n);
    if (rec[id].subsSeries.length < 40) rec[id].subsSeries.push(n);
  }
  if (subs) subs.forEach((fn) => fn());
}

// Push (or clear, when override == null) the live geometry for one object id.
export function setLiveResize(id, override) {
  if (!id) return;
  if (override == null) overrides.delete(id);
  else overrides.set(id, override);
  emit(id);
}

export function clearLiveResize(id) {
  if (!id) return;
  if (overrides.delete(id)) emit(id);
}

export function getLiveResize(id) {
  return overrides.get(id) || null;
}

// TEMP PROBE helper. In the console: __live() after one resize gesture.
if (typeof window !== "undefined") {
  window.__probeLive = true;
  window.__live = () => {
    const stats = window.__liveStats || {};
    const who = window.__liveWho || {};
    const out = Object.entries(stats).map(([id, s]) => ({
      id: id.slice(0, 8),
      emits: s.emits,
      maxSubscribers: s.maxSubs,
      // A climbing series == components remounting every frame (the smoking gun).
      subscriberTrend: s.subsSeries.slice(0, 12).join(","),
      subscribers: Array.from(who[id] || []),
    }));
    console.log("LIVE_PROBE " + JSON.stringify(out, null, 1));
    window.__liveStats = {};
    return out;
  };
}

function subscribe(id, fn) {
  let subs = listeners.get(id);
  if (!subs) {
    subs = new Set();
    listeners.set(id, subs);
  }
  subs.add(fn);
  return () => {
    subs.delete(fn);
    if (subs.size === 0) listeners.delete(id);
  };
}

// Hook: returns the current override for `id` (or null). Re-renders the calling
// component ONLY when THIS id's override changes — other objects are unaffected,
// and MainCanvas (which never calls this) never re-renders from a resize.
export function useLiveResize(id) {
  // TEMP PROBE: record which component subscribed, so a runaway re-render can be
  // attributed to a named file instead of guessed at. Captured once per mount.
  if (typeof window !== "undefined" && window.__probeLive) {
    const who = new Error().stack || "";
    const reg = (window.__liveWho = window.__liveWho || {});
    reg[id] = reg[id] || new Set();
    // Second stack frame = the component that called useLiveResize.
    const line = who.split("\n")[2] || "";
    reg[id].add(line.trim().slice(0, 140));
  }
  return useSyncExternalStore(
    (cb) => subscribe(id, cb),
    () => getLiveResize(id),
    () => null
  );
}
